import React, { useEffect, useState } from "react";
import "../../styles/StaticPages.css";
import shortifyImage from "../../assets/images/shortify-about.jpeg";

const AboutShortify: React.FC = () => {
  const [activeId, setActiveId] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll("section[id]");
      let currentId = "";

      sections.forEach((section) => {
        const top = section.getBoundingClientRect().top;
        if (top <= 200) {
          currentId = section.id;
        }
      });

      if (currentId) {
        setActiveId(currentId);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <div className="static-hero">
        <div className="static-hero-inner">
          <h1>About Shortfy</h1>
        </div>
      </div>
      <div className="static-page-container with-sidebar">
        <aside className="on-this-page">
          <h2>On this page:</h2>
          <ul>
            <li>
              <a
                href="#what-is"
                className={activeId === "what-is" ? "active" : ""}
              >
                What is Shortfy?
              </a>
            </li>
            <li>
              <a
                href="#why-use"
                className={activeId === "why-use" ? "active" : ""}
              >
                Why use Shortfy?
              </a>
            </li>
            <li>
              <a
                href="#who-is-it-for"
                className={activeId === "who-is-it-for" ? "active" : ""}
              >
                Who is it for?
              </a>
            </li>
            <li>
              <a
                href="#powered-by"
                className={activeId === "powered-by" ? "active" : ""}
              >
                Powered by AIS
              </a>
            </li>
            <li>
              <a
                href="#accessibility"
                className={activeId === "accessibility" ? "active" : ""}
              >
                Accessibility
              </a>
            </li>
            <li>
              <a
                href="#feedback"
                className={activeId === "feedback" ? "active" : ""}
              >
                Need help or feedback?
              </a>
            </li>
          </ul>
        </aside>

        <div className="static-content">
          <div className="static-image-wrapper">
            <img
              src={shortifyImage}
              alt="People using Shortify"
              className="about-image"
            />
          </div>

          <section id="what-is">
            <h2>What is Shortfy?</h2>
            <p>
              Shortfy is CGI's internal URL-shortening platform that turns long,
              complex links into short, memorable URLs. It centralizes link
              creation and management, enabling teams to generate secure,
              standardized shortcuts within seconds, update destinations without
              breaking references, and track usage to retire outdated links.
              Beyond individual links, Shortfy also bundles multiple URLs into
              one professional all-in-one link and offers extra features such as
              custom-designed logos in QR codes and embedded Wi-Fi
              credentials—helping CGI partners share resources faster, clearer,
              and more reliably.
            </p>
          </section>

          <section id="why-use">
            <h2>Why use Shortfy?</h2>
            <ul>
              <li>
                <strong>Easy-to-use interface:</strong>
                <br />
                The platform is intuitive and user-friendly, so you can create
                and manage short links in seconds—no technical skills required.
              </li>
              <li>
                <strong>Secure internal access:</strong>
                <br />
                Shortfy is only accessible within CGI, ensuring that your links
                and data stay safe and private.
              </li>
              <li>
                <strong>Link tracking and management:</strong>
                <br />
                View analytics on your links and easily edit or deactivate them
                as your needs change.
              </li>
              <li>
                <strong>Individual logo upload:</strong>
                <br />
                Customize QR codes with project or team branding.
              </li>
              <li>
                <strong>CGI-wide or individual branding:</strong>
                <br />
                Flexible use for corporate or team-specific identity.
              </li>
              <li>
                <strong>Central Link Collection:</strong>
                <br />
                Allows CGI partners to bundle multiple URLs into one
                professional, all-in-one link.
              </li>
              <li>
                <strong>Wi-Fi Info:</strong>
                <br />
                Share wireless access credentials directly via QR codes,
                enabling instant device connection.
              </li>
              <li>
                <strong>Effortless sharing across channels:</strong>
                <br />
                Generate short URLs and QR codes that look professional and are
                easy to share—whether in presentations, printed materials,
                email-signatures, chats, or documentation.
              </li>
              <li>
                <strong>Reduces errors and confusion:</strong>
                <br />
                Say goodbye to broken links, hard-to-read URLs, and confusing
                document references.
              </li>
            </ul>
          </section>

          <section id="who-is-it-for">
            <h2>Who is it for?</h2>
            <p>
              Shortfy is for every CGI partner who shares links inside the
              organization—whether you are a developer referencing documentation
              in Teams, a consultant adding resources to a slide deck, or an HR
              specialist guiding new hires to onboarding forms. Anyone
              frustrated by long URLs can replace them with clean, memorable
              aliases. CGI partners benefit from version-proof links that can be
              updated as content evolves, can create compact QR codes for
              print-designs and send standardized, human-readable shortcuts to
              knowledge-base articles. In short, if you need to distribute
              internal resources quickly and reliably, Shortfy is tailored for
              you.
            </p>
          </section>

          <section id="powered-by">
            <h2>Powered by AIS (Agile Innovation Squad)</h2>
            <p>
              The AIS is a dedicated team within CGI, specialized in developing,
              deploying, and continuously enhancing internal digital solutions.
              Our mission is to empower CGI partners by streamlining processes,
              boosting productivity, and fostering a culture of agility and
              technological excellence.
            </p>
            <p>
              Acting like an internal startup, AIS applies agile methods to
              deliver user-centric platforms such as Shortfy. In close
              collaboration with CGI partners, the team identifies challenges,
              prototypes solutions, and scales impactful tools—optimizing
              workflows while enabling new business ideas. With Shortfy and
              other platforms, AIS simplifies daily work, strengthens
              collaboration, and helps CGI partners focus on delivering client
              value and driving collective success.
            </p>
          </section>

          <section id="accessibility">
            <h2>Accessibility</h2>
            <p>
              We are committed to making Shortfy accessible to everyone,
              regardless of ability or background. Our team follows best
              practices in accessibility design, including semantic HTML, clear
              color contrast, keyboard navigation support, and compatibility
              with screen readers. If you experience any difficulties using
              Shortfy, encounter accessibility barriers, or have suggestions on
              how we can improve the user experience, we encourage you to let us
              know. We value your feedback and are dedicated to creating an
              inclusive tool for all CGI partners. Please{" "}
              <a href="mailto:AIS.DE@cgi.com?subject=Accessibility Feedback">
                contact our Help Desk
              </a>{" "}
              with your comments or concerns so we can address any issues
              promptly.
            </p>
          </section>

          <section id="feedback">
            <h2>Need help or feedback?</h2>
            <p>
              The Shortfy Help Desk supports you with questions, feature
              requests, or issues. Your feedback helps us improve the platform
              and keep Shortfy inclusive for all CGI partners.
            </p>
            <ul>
              <li>
                <strong>Contact:</strong>{" "}
                <a href="mailto:AIS.DE@cgi.com?subject=Shortfy Support Request">
                  Shortfy Support Team
                </a>
              </li>
            </ul>
          </section>
        </div>
      </div>
    </>
  );
};

export default AboutShortify;
